import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Chip,
  Checkbox,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import { SaveAlt } from '@mui/icons-material'
import PayrollHeader from './PayrollHeader'
import PayrollTabs from './PayrollTabs'
import { salaryConfigAPI } from '../../services/api'

const nativeInputStyle = { width: 120, fontSize: '14px', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', boxSizing: 'border-box' }

function ConfigureSalary() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveMessage, setSaveMessage] = useState('')
  const [saving, setSaving] = useState(false)

  const [salaryValues, setSalaryValues] = useState({})
  const [consider30DaysValues, setConsider30DaysValues] = useState({})
  const [dirtyEmployees, setDirtyEmployees] = useState(new Set())

  const [search, setSearch] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [groupFilter, setGroupFilter] = useState('')

  useEffect(() => {
    loadRows()
  }, [])

  async function loadRows() {
    setLoading(true)
    setError('')
    try {
      const response = await salaryConfigAPI.getAll()
      setRows(response.data)
      const initial = {}
      const initialConsider30 = {}
      response.data.forEach((row) => {
        initial[row.emp_id] = row.gross_salary ?? ''
        initialConsider30[row.emp_id] = !!row.consider_30_days
      })
      setSalaryValues(initial)
      setConsider30DaysValues(initialConsider30)
      setDirtyEmployees(new Set())
    } catch (err) {
      setError('Failed to load employees.')
    } finally {
      setLoading(false)
    }
  }

  function markDirty(empId) {
    setDirtyEmployees((prev) => new Set([...prev, empId]))
  }

  function handleSalaryChange(empId, value) {
    setSalaryValues((prev) => ({ ...prev, [empId]: value }))
    markDirty(empId)
  }

  function handleConsider30DaysChange(empId, checked) {
    setConsider30DaysValues((prev) => ({ ...prev, [empId]: checked }))
    markDirty(empId)
  }

  const departments = useMemo(
    () => Array.from(new Set(rows.map((row) => row.department).filter(Boolean))).sort(),
    [rows]
  )
  const groups = useMemo(
    () => Array.from(new Set(rows.map((row) => row.group).filter(Boolean))).sort(),
    [rows]
  )

  const filteredRows = rows
    .filter((row) => {
      if (search && !row.emp_name.toLowerCase().includes(search.toLowerCase())) return false
      if (departmentFilter && row.department !== departmentFilter) return false
      if (groupFilter && row.group !== groupFilter) return false
      return true
    })
    // Active employees first, then inactive - stable sort, so relative
    // order within each group (e.g. by name) is otherwise unchanged.
    .sort((a, b) => Number(b.active) - Number(a.active))

  async function handleSaveAll() {
    const dirtyRows = rows.filter((row) => dirtyEmployees.has(row.emp_id))
    if (dirtyRows.length === 0) {
      setSaveMessage('No changes to save.')
      setTimeout(() => setSaveMessage(''), 3000)
      return
    }

    setSaving(true)
    setSaveMessage('')
    try {
      const items = dirtyRows.map((row) => ({
        emp_id: row.emp_id,
        emp_name: row.emp_name,
        department: row.department,
        gross_salary: Number(salaryValues[row.emp_id]) || 0,
        consider_30_days: !!consider30DaysValues[row.emp_id],
      }))
      const response = await salaryConfigAPI.bulkSave(items)
      setSaveMessage(`Saved ${response.data.saved} record(s).`)
      setDirtyEmployees(new Set())
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.detail || 'Unknown error'))
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  return (
    <>
      <PayrollHeader />
      <PayrollTabs />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Configure Salary</Typography>
          <Button
            variant="contained"
            startIcon={<SaveAlt />}
            onClick={handleSaveAll}
            disabled={saving || dirtyEmployees.size === 0}
          >
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {saveMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveMessage('')}>{saveMessage}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search by name"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            label="Department"
            size="small"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Pay Group"
            size="small"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Pay Groups</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group} value={group}>{group}</MenuItem>
            ))}
          </TextField>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Pay Group</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gross Salary</TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'normal', maxWidth: 140 }}>
                  <Tooltip title="When checked, this employee's Pay Salary always divides Gross Salary by 30 instead of the real days in the month - keeps their effective daily rate stable every month instead of drifting between 28/30/31-day months.">
                    <span>Assume Month days as 30<br />for Calculation</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow><TableCell colSpan={6}>No employees found.</TableCell></TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow key={row.emp_id} hover>
                    <TableCell>{row.emp_name}</TableCell>
                    <TableCell>{row.department || '-'}</TableCell>
                    <TableCell>{row.group || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.active ? 'Active' : 'Inactive'}
                        color={row.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={salaryValues[row.emp_id] ?? ''}
                        onChange={(e) => handleSalaryChange(row.emp_id, e.target.value)}
                        style={nativeInputStyle}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={!!consider30DaysValues[row.emp_id]}
                        onChange={(e) => handleConsider30DaysChange(row.emp_id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  )
}

export default ConfigureSalary
